import { useState } from 'react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getStripePromise } from '@/lib/stripe'
import { formatUserFacingError } from '@/lib/errors'

interface InnerProps {
  amountCents: number
  currency: string
  onSuccess: () => void
}

function PaymentForm({ amountCents, currency, onSuccess }: InnerProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return

    setProcessing(true)
    setError(null)

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(
        formatUserFacingError(
          stripeError,
          'We could not complete your payment. Please check your details and try again.',
          'payments/application-confirmation'
        )
      )
      setProcessing(false)
    } else {
      onSuccess()
    }
  }

  const formatted = (() => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(amountCents / 100)
    } catch {
      return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`
    }
  })()

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <PaymentElement />
      {error && (
        <p className="text-sm text-destructive" role="alert" aria-live="polite">
          {error}
        </p>
      )}
      <Button type="submit" disabled={!stripe || !elements || processing} className="w-full">
        {processing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing…
          </>
        ) : (
          `Pay ${formatted} & Submit`
        )}
      </Button>
    </form>
  )
}

interface Props {
  clientSecret: string
  amountCents: number
  currency: string
  onSuccess: () => void
}

export function ApplicationPaymentStep({ clientSecret, amountCents, currency, onSuccess }: Props) {
  return (
    <Elements
      stripe={getStripePromise()}
      options={{ clientSecret, appearance: { theme: 'stripe' } }}
    >
      <PaymentForm amountCents={amountCents} currency={currency} onSuccess={onSuccess} />
    </Elements>
  )
}
